/**
 * Smokeball migration — directApiClient mutations (LF-3214)
 */

'use client';

import { ActionResult } from '@/lib/types/actions';
import { directApiClient } from '@/lib/api/direct-client';
import type {
  MigrateFilesRequest,
  MigrateFilesResponse,
  MigrationProgressResponse,
  SmokeballSearchParams,
  SmokeballSearchResponse
} from './types';

/**
 * List the matter's PMS (Smokeball) documents available to import. Reuses the
 * existing document-search endpoint — shared by the AI Document Import modal
 * and the Redemption "Attach from Smokeball" picker (LF-5952).
 */
export async function searchMigrationFilesAction(
  matterId: number,
  params?: SmokeballSearchParams
): Promise<ActionResult<SmokeballSearchResponse>> {
  try {
    const query = new URLSearchParams();
    if (params?.query) query.append('query', params.query);
    if (params?.limit != null) query.append('limit', String(params.limit));
    if (params?.offset != null) query.append('offset', String(params.offset));
    if (params?.updatedSince) query.append('updatedSince', params.updatedSince);
    const qs = query.toString();

    const data = await directApiClient.get<SmokeballSearchResponse>(
      `/matters/${matterId}/ai-analysis/pms-migration/document-search${qs ? `?${qs}` : ''}`
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to search PMS documents'
    };
  }
}

export async function migrateFilesAction(
  matterId: number,
  request: MigrateFilesRequest
): Promise<ActionResult<MigrateFilesResponse>> {
  try {
    const fileIds = request.fileSelections.map((f) => f.externalId).join(',');
    const data = await directApiClient.post<MigrateFilesResponse>(
      `/matters/${matterId}/ai-analysis/pms-migration/migrate?fileIds=${fileIds}`
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to migrate files'
    };
  }
}

export async function getMigrationProgressAction(
  matterId: number
): Promise<ActionResult<MigrationProgressResponse>> {
  try {
    const data = await directApiClient.get<MigrationProgressResponse>(
      `/matters/${matterId}/ai-analysis/pms-migration/progress`
    );
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get progress'
    };
  }
}
